import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";

export default function BuildSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const provider = params.get("provider");
  const sessionId = params.get("session_id");
  const paypalOrderId = params.get("token"); // PayPal appends ?token=ORDER_ID&PayerID=...
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [order, setOrder] = useState(null);
  const attempts = useRef(0);

  const loadOrder = async () => {
    try {
      const { data } = await api.get("/build/orders");
      const match = (data.orders || []).find((o) =>
        (sessionId && o.session_id === sessionId) || (paypalOrderId && o.paypal_order_id === paypalOrderId));
      if (match) setOrder(match);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    let alive = true;
    // PayPal: capture the approved order server-side (source of truth).
    if (provider === "paypal" && paypalOrderId) {
      (async () => {
        try {
          const { data } = await api.post("/payments/paypal/capture", { paypal_order_id: paypalOrderId });
          if (!alive) return;
          if (data.payment_status === "paid") { setState("paid"); loadOrder(); track("Checkout Completed", { provider: "paypal" }); }
          else setState("pending");
        } catch { if (alive) setState("error"); }
      })();
      return () => { alive = false; };
    }
    if (!sessionId) { setState("error"); return; }
    const poll = async () => {
      if (!alive) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("paid"); loadOrder(); track("Checkout Completed", { session_id: sessionId }); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch { /* keep trying */ }
      attempts.current += 1;
      if (attempts.current > 8) { setState("pending"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, provider, paypalOrderId]);

  const pid = order?.package_id;
  const isBlueprint = pid === "blueprint_pro" || (!pid);
  const paidHeadline = pid === "launch_page" ? "Launch Page ordered"
    : pid === "business_site" ? "Business Site ordered"
    : pid === "blueprint_pro" ? "Blueprint Pro unlocked"
    : "Payment confirmed";
  const paidBody = isBlueprint
    ? "Your developer-ready specification is ready to download. It's credited toward a future build."
    : "Thanks! We've started production on your build. You can track its status any time in Build Studio.";

  return (
    <AppShell>
      <div className="mx-auto max-w-lg px-8 py-20 text-center ltifade" data-testid="build-success">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto animate-spin text-brand" size={34} />
            <p className="mt-5 font-heading text-xl font-semibold">Confirming your payment…</p>
            <p className="mt-2 text-sm text-slate-500">This only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-emerald-50"><CheckCircle2 size={32} className="text-emerald-500" /></span>
            <p className="mt-5 font-heading text-2xl font-semibold" data-testid="build-success-headline">{paidHeadline}</p>
            <p className="mt-2 text-slate-600">{paidBody}</p>
            <button data-testid="go-build-studio-btn" onClick={() => navigate("/build")}
              className="mt-6 inline-flex items-center gap-2 rounded-lg bg-brand px-6 py-3 text-sm font-medium text-white transition-all duration-200 hover:bg-brand-hover">
              {isBlueprint ? "Open your blueprint" : "Back to Build Studio"} <ArrowRight size={16} />
            </button>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="mx-auto animate-spin text-brand" size={30} />
            <p className="mt-5 font-heading text-xl font-semibold">Still confirming…</p>
            <p className="mt-2 text-sm text-slate-500">Payment can take a minute. Refresh Build Studio shortly, your access unlocks automatically.</p>
            <button onClick={() => navigate("/build")} className="mt-6 rounded-lg border border-brand-line px-5 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50">Back to Build Studio</button>
          </>
        )}
        {state === "error" && (
          <>
            <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-red-50"><XCircle size={32} className="text-red-500" /></span>
            <p className="mt-5 font-heading text-xl font-semibold">We couldn't confirm that payment</p>
            <p className="mt-2 text-slate-600">No charge is applied until payment is confirmed. You can try again from Build Studio.</p>
            <button onClick={() => navigate("/build")} className="mt-6 rounded-lg bg-brand px-6 py-3 text-sm font-medium text-white hover:bg-brand-hover">Back to Build Studio</button>
          </>
        )}
      </div>
    </AppShell>
  );
}
